import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LifeBuoy, ArrowLeft, ArrowRight, Loader2, CheckCircle2, X,
  Trophy, Volume2, Sparkles, RotateCcw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { AmbientCanvas } from "@/components/effects/ambient-canvas";
import { PageFrame } from "@/components/page-frame";
import { AppTopBar } from "@/components/app-topbar";
import {
  fetchWrongWords, fetchAttemptedVocab, saveProgress,
  type VocabWord
} from "@/lib/queries";
import { playWordClick, speakWord } from "@/lib/audio";
import { cn } from "@/lib/utils";

const SESSION_SIZE = 5;

type Phase = "loading" | "no-vocab" | "learn" | "test" | "results";
type Option = { id: string; label: string; isCorrect: boolean };

export default function Assistance() {
  const nav = useNavigate();
  const [phase, setPhase] = React.useState<Phase>("loading");
  const [wrong, setWrong] = React.useState<VocabWord[]>([]);
  const [pool, setPool]   = React.useState<VocabWord[]>([]);

  const [idx, setIdx] = React.useState(0);
  const [options, setOptions] = React.useState<Option[]>([]);
  const [picked, setPicked]   = React.useState<string | null>(null);
  const [firstTry, setFirstTry] = React.useState(0);
  const [recovered, setRecovered] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setPhase("loading");
      const [wrongWords, allAttempted] = await Promise.all([
        fetchWrongWords(SESSION_SIZE),
        fetchAttemptedVocab()
      ]);
      if (cancelled) return;
      if (wrongWords.length === 0) { setPhase("no-vocab"); return; }
      setWrong(wrongWords);
      setPool(allAttempted);
      setIdx(0);
      setFirstTry(0);
      setRecovered(new Set());
      setPhase("learn");
      if (wrongWords[0]) void speakWord(wrongWords[0]);
    })();
    return () => { cancelled = true; };
  }, []);

  function startMicroTest() {
    const word = wrong[idx];
    if (!word) return;
    const distractors = shuffle(pool.filter(w => w.id !== word.id && w.english !== word.english)).slice(0, 3);
    const opts: Option[] = shuffle([
      { id: String(word.id), label: word.english, isCorrect: true },
      ...distractors.map(w => ({ id: String(w.id), label: w.english, isCorrect: false }))
    ]);
    setOptions(opts);
    setPicked(null);
    setPhase("test");
    if (word.arabic) void speakWord(word);
  }

  function pickAnswer(opt: Option) {
    if (picked) return;
    setPicked(opt.id);
    const word = wrong[idx];
    if (!word) return;
    const isCorrect = opt.isCorrect;
    const isFirstTry = !recovered.has(String(word.id));
    void saveProgress({
      word_id: word.id,
      correct: isCorrect,
      mode:    "assistance",
      assisted: true
    });
    if (isCorrect) {
      if (isFirstTry) setFirstTry(n => n + 1);
      const next = new Set(recovered);
      next.add(String(word.id));
      setRecovered(next);
      window.setTimeout(() => {
        const ni = idx + 1;
        if (ni >= wrong.length) setPhase("results");
        else { setIdx(ni); setPhase("learn"); if (wrong[ni]) void speakWord(wrong[ni]); }
      }, 600);
    } else {
      // Wrong → return to flashcard for the same word.
      window.setTimeout(() => { setPhase("learn"); }, 800);
    }
  }

  const card = wrong[idx];

  return (
    <PageFrame surface="app">
      <AppTopBar />
      <div className="relative">
        <AmbientCanvas className="absolute inset-0 -z-10 w-full h-[900px] pointer-events-none" density={40} />

        <div className="relative mx-auto max-w-3xl px-4 md:px-6 pt-10 pb-32">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/dashboard"><ArrowLeft className="w-4 h-4" /> Back</Link>
            </Button>
            {(phase === "learn" || phase === "test") && <Badge variant="accent">Word {idx + 1} / {wrong.length}</Badge>}
          </div>

          <BlurReveal>
            <div className="flex items-center gap-3 mb-2">
              <LifeBuoy className="w-6 h-6 text-accent" />
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Assistance</h1>
            </div>
            <p className="text-fg-2"><span className="text-accent">Wrong words</span> · {wrong.length || "no"} to recover</p>
          </BlurReveal>

          {(phase === "learn" || phase === "test") && (
            <div className="mt-4 h-1.5 rounded-full bg-bg-2 overflow-hidden">
              <div className="h-full bg-accent transition-all" style={{ width: `${(idx / wrong.length) * 100}%` }} />
            </div>
          )}

          <div className="mt-8">
            {phase === "loading" && <Spinner />}

            {phase === "no-vocab" && (
              <Card className="text-center"><CardContent className="py-12">
                <p className="text-fg-2">Nothing to recover — everything you've practised is already right!</p>
                <Button className="mt-4" asChild><Link to="/learn">Practise more</Link></Button>
              </CardContent></Card>
            )}

            {phase === "learn" && card && (
              <BlurReveal>
                <Card className="text-center relative overflow-hidden">
                  <div aria-hidden className="absolute -top-12 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full bg-accent/15 blur-3xl" />
                  <CardContent className="relative py-12">
                    <div className="text-xs uppercase tracking-widest text-fg-3 mb-3">Learn this word</div>
                    <div className="text-6xl md:text-7xl font-bold text-accent" style={{ fontFamily: "'Noto Sans Arabic',serif", direction: "rtl" }}>
                      {card.arabic}
                    </div>
                    <div className="mt-3 text-xl text-fg-2">{card.transliteration}</div>
                    <div className="mt-1 text-2xl font-medium">{card.english}</div>
                    <Button variant="outline" size="md" className="mt-6" onClick={() => playWordClick(card)}>
                      <Volume2 className="w-4 h-4" /> Play audio
                    </Button>
                    <div className="mt-8">
                      <Button size="lg" onClick={startMicroTest}>Got it — test me <Sparkles className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              </BlurReveal>
            )}

            {phase === "test" && card && (
              <BlurReveal>
                <Card className="text-center">
                  <CardContent className="py-10">
                    <div className="text-xs uppercase tracking-widest text-fg-3">What does this mean?</div>
                    <div className="mt-5 text-6xl md:text-7xl font-bold text-accent" style={{ fontFamily: "'Noto Sans Arabic',serif", direction: "rtl" }}>
                      {card.arabic}
                    </div>
                    {card.transliteration && <div className="mt-2 text-lg text-fg-2">{card.transliteration}</div>}

                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {options.map((opt, i) => {
                        const isPicked = picked === opt.id;
                        const showRight = picked !== null && opt.isCorrect;
                        const showWrong = isPicked && !opt.isCorrect;
                        return (
                          <button key={opt.id} type="button"
                            disabled={picked !== null}
                            onClick={() => pickAnswer(opt)}
                            className={cn(
                              "rounded-2xl border px-5 py-4 text-left transition-colors flex items-start gap-3",
                              !picked && "border-line bg-bg hover:bg-bg-3 hover:border-line-2",
                              showRight && "border-ok/40 bg-ok/10 text-ok",
                              showWrong && "border-bad/40 bg-bad/10 text-bad",
                              picked && !showRight && !showWrong && "border-line bg-bg-3/30 text-fg-2 opacity-60"
                            )}>
                            <span className="text-xs uppercase tracking-widest text-fg-3 shrink-0">{String.fromCharCode(65 + i)}</span>
                            <span className="flex-1">{opt.label}</span>
                            {showRight && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                            {showWrong && <X className="w-4 h-4 shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </BlurReveal>
            )}

            {phase === "results" && (
              <BlurReveal>
                <Card>
                  <CardContent className="text-center py-10">
                    <Trophy className="w-12 h-12 text-accent mx-auto mb-3" />
                    <div className="text-xs uppercase tracking-widest text-fg-3">Recovery complete</div>
                    <h2 className="text-4xl font-bold tracking-tight mt-2">
                      {firstTry} / {wrong.length} <span className="text-fg-3 font-normal">first try</span>
                    </h2>
                    <p className="mt-3 text-sm text-fg-2 max-w-md mx-auto">
                      All {wrong.length} words recovered. They've been re-marked in your progress so they won't
                      show up here next time unless you slip again.
                    </p>
                    <p className="mt-2 text-xs text-fg-3">
                      Assistance doesn't award points — but it does keep your mastery counts honest.
                    </p>

                    <div className="mt-7 flex flex-wrap gap-3 justify-center">
                      <Button size="lg" onClick={() => nav(0)}><RotateCcw className="w-4 h-4" /> Run again</Button>
                      <Button variant="outline" size="lg" asChild><Link to="/dashboard">Dashboard <ArrowRight className="w-4 h-4" /></Link></Button>
                    </div>
                  </CardContent>
                </Card>
              </BlurReveal>
            )}
          </div>
        </div>
      </div>
    </PageFrame>
  );
}

function Spinner() {
  return (
    <div className="py-16 flex items-center justify-center gap-2 text-fg-3">
      <Loader2 className="w-4 h-4 animate-spin text-accent" /> Loading…
    </div>
  );
}

function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
